import { Badge, Card, Group, Stack, Title } from '@mantine/core';
import type { CatalogEntry } from '../api/types';
import { InfoRow } from './InfoRow';

interface VendorInfoCardProps {
    vendor: CatalogEntry;
}

/**
 * Read-only display of catalog fields, split into vendor-identity and
 * product-identity sections. Mirrors CandidateInfoCard so the Company Catalog
 * detail and the Candidate Catalog detail read the same way — the compound
 * catalog row carries both a vendor (company) and a single product, and the two
 * were previously mixed under one "Vendor Info" heading.
 */
export const VendorInfoCard = ({ vendor }: VendorInfoCardProps): React.JSX.Element => {
    return (
        <Card withBorder radius="md" padding="lg" data-testid="VendorInfoCard">
            <Group justify="space-between" mb="md">
                <Title order={4}>Vendor Info</Title>
                {vendor.is_default ? (
                    <Badge variant="light" color="blue">
                        Default product
                    </Badge>
                ) : null}
            </Group>
            <Stack gap="xs">
                <InfoRow label="Name" value={vendor.name} />
                <InfoRow label="Domain" value={vendor.domain} />
                <InfoRow label="Website" value={vendor.website_url} isLink />
                <InfoRow label="Trust Center" value={vendor.trust_center_url} isLink />
                <InfoRow label="Open Trust" value={vendor.open_trust_url} isLink />
                <InfoRow label="Logo" value={vendor.logo} isLink />
                <InfoRow label="Favicon" value={vendor.favicon} isLink />
                <InfoRow label="Provider" value={vendor.provider_abbreviation} />
                <InfoRow label="Organization ID" value={vendor.organization_id} />
            </Stack>

            <Title order={4} mt="lg" mb="md">
                Product Info
            </Title>
            <Stack gap="xs">
                <InfoRow label="Product Name" value={vendor.product_name} />
                <InfoRow label="Slug" value={vendor.slug} />
                <InfoRow label="Is Default" value={vendor.is_default ? 'Yes' : 'No'} />
                <InfoRow label="Catalog ID" value={vendor.id} />
                <InfoRow label="Searchable Key" value={vendor.searchable_key} />
                <InfoRow label="Metadata" value={vendor.metadata} />
                <InfoRow label="Tenants" value={String(vendor.tenant_count)} />
            </Stack>
        </Card>
    );
};
